/**
 * Google `Autocomplete` adapter — uses Places (New) via
 * `AutocompleteSuggestion.fetchAutocompleteSuggestions`, with a session
 * token for billing (per Google's recommendation — same pattern as the
 * Mapbox Search Box session-token grouping). Session is concluded by calling
 * `Place.fetchFields` on selection, which Google bills as a single session.
 *
 * Render-prop API only — the visible input chrome stays under the consumer's
 * control. Adapter owns query state, debouncing, and the suggestion fetch.
 *
 * Note: This requires the Google Maps JS API to have been loaded by an
 * ancestor `APIProvider`. The `GoogleMapView` adapter mounts one, so as
 * long as Autocomplete is used inside a LocationPicker that also renders
 * a MapView, this works out of the box. If you ever use Autocomplete
 * standalone, wrap it in `<APIProvider>` yourself.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type * as React from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import type {
  AutocompleteProps,
  AutocompleteRenderApi,
  AutocompleteSuggestion,
  AutocompleteSelection,
} from '../../types';
import { getGoogleMapsApiKey } from './token';

// Per @vis.gl docs, multiple APIProvider instances are safe as long as their
// props are identical — only the first to render actually loads the Maps API.
// Keep this in sync with GoogleMapView's APIProvider props or library loads
// will conflict. Follow-up #350: lift APIProvider to a single connector root.
const API_PROVIDER_LIBRARIES = ['marker', 'places', 'geocoding'] as const;

const GoogleAutocompleteInner: React.FC<AutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  country = 'PR',
  proximity = { lat: 18.2208, lng: -66.5901 },
  children,
}) => {
  const placesLib = useMapsLibrary('places');
  const [internalQuery, setInternalQuery] = useState(value ?? '');
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Stash the raw google `PlacePrediction` objects keyed by placeId so the
  // selection handler can call `toPlace().fetchFields()` without re-querying.
  const rawPredictionsRef = useRef<Map<string, google.maps.places.PlacePrediction>>(new Map());
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  // Lazily create the session token once the places library is available.
  // A new token is created after each selection to start a fresh billing
  // session per Google's docs.
  useEffect(() => {
    if (!placesLib) return;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
    }
  }, [placesLib]);

  const query = value !== undefined ? value : internalQuery;
  const setQuery = useCallback(
    (q: string) => {
      if (value === undefined) setInternalQuery(q);
      onChange?.(q);
    },
    [value, onChange],
  );

  useEffect(() => {
    const q = query.trim();
    if (!q || q.length < 2 || !placesLib) {
      setSuggestions([]);
      rawPredictionsRef.current.clear();
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setIsLoading(true);
      try {
        const request: google.maps.places.AutocompleteRequest = {
          input: q,
          language: 'en',
          region: country,
          includedRegionCodes: [country],
          // locationBias = soft preference for nearby results.
          locationBias: {
            center: { lat: proximity.lat, lng: proximity.lng },
            radius: 50000, // 50km — roughly half of PR
          },
          sessionToken: sessionTokenRef.current ?? undefined,
        };
        const { suggestions: raw } = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        if (cancelled) return;
        rawPredictionsRef.current.clear();
        const mapped: AutocompleteSuggestion[] = [];
        raw.forEach((s) => {
          const pred = s.placePrediction;
          if (!pred) return;
          rawPredictionsRef.current.set(pred.placeId, pred);
          mapped.push({
            id: pred.placeId,
            name: pred.mainText?.text ?? pred.text.text,
            description: pred.secondaryText?.text,
          });
        });
        setSuggestions(mapped);
      } catch (err) {
        if (!cancelled) {
          console.error('[maps:google] autocomplete suggest failed:', err);
          setSuggestions([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, country, proximity.lat, proximity.lng, placesLib]);

  const selectSuggestion = useCallback(async (s: AutocompleteSuggestion) => {
    if (!placesLib) return;
    const pred = rawPredictionsRef.current.get(s.id);
    if (!pred) return;
    setIsLoading(true);
    try {
      const place = pred.toPlace();
      // fetchFields concludes the autocomplete session (billable). Request
      // the minimum needed to populate the selection.
      await place.fetchFields({ fields: ['location', 'formattedAddress', 'displayName'] });
      const location = place.location;
      if (!location) return;
      const selection: AutocompleteSelection = {
        lat: location.lat(),
        lng: location.lng(),
        address: place.formattedAddress ?? '',
        placeName: place.displayName ?? undefined,
        placeId: pred.placeId,
      };
      setSuggestions([]);
      rawPredictionsRef.current.clear();
      // Rotate the session token now that fetchFields has concluded the
      // billing session.
      sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
      onSelectRef.current(selection);
    } catch (err) {
      console.error('[maps:google] place fetchFields failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [placesLib]);

  const api: AutocompleteRenderApi = { query, setQuery, suggestions, isLoading, selectSuggestion };
  return <>{children ? children(api) : null}</>;
};

export const GoogleAutocomplete: React.FC<AutocompleteProps> = (props) => {
  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    const emptyApi: AutocompleteRenderApi = {
      query: props.value ?? '',
      setQuery: () => {},
      suggestions: [],
      isLoading: false,
      selectSuggestion: async () => {},
    };
    return <>{props.children ? props.children(emptyApi) : null}</>;
  }
  return (
    <APIProvider apiKey={apiKey} libraries={[...API_PROVIDER_LIBRARIES]}>
      <GoogleAutocompleteInner {...props} />
    </APIProvider>
  );
};
